import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, X, Save, Clock, Loader2 } from 'lucide-react';
import { shiftService } from '../services/shiftService';

interface Shift {
  id: string;
  name: string;
  code: string;
  startTime: string;
  endTime: string;
  breakDuration: number;
  type: 'daily' | 'night' | 'rotating';
  color: string;
  daysOfWeek: string[];
  status: 'active' | 'inactive';
  notes?: string;
}

const DAYS_OF_WEEK = [
  { value: 'Monday', label: 'Pazartesi' },
  { value: 'Tuesday', label: 'Salı' },
  { value: 'Wednesday', label: 'Çarşamba' },
  { value: 'Thursday', label: 'Perşembe' },
  { value: 'Friday', label: 'Cuma' },
  { value: 'Saturday', label: 'Cumartesi' },
  { value: 'Sunday', label: 'Pazar' },
];

const SHIFT_COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#8B5CF6',
  '#EF4444',
  '#06B6D4',
  '#EC4899',
];

const DEFAULT_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

const mapApiShiftToUi = (shift: any): Shift => ({
  id: String(shift.id),
  name: shift.name || '',
  code: shift.code || '',
  startTime: shift.start_time || shift.startTime || '',
  endTime: shift.end_time || shift.endTime || '',
  breakDuration: Number(shift.break_duration_minutes || shift.breakDuration || 0),
  type: shift.type || 'daily',
  color: shift.color || SHIFT_COLORS[0],
  daysOfWeek: Array.isArray(shift.days_of_week)
    ? shift.days_of_week
    : typeof shift.days_of_week === 'string'
      ? (() => {
          try {
            const parsed = JSON.parse(shift.days_of_week);
            return Array.isArray(parsed) ? parsed : DEFAULT_DAYS;
          } catch {
            return DEFAULT_DAYS;
          }
        })()
      : Array.isArray(shift.daysOfWeek)
        ? shift.daysOfWeek
        : DEFAULT_DAYS,
  status: shift.status || 'active',
  notes: shift.notes || '',
});

const mapUiShiftToApi = (shift: Partial<Shift>) => ({
  name: shift.name,
  code: shift.code,
  startTime: shift.startTime,
  endTime: shift.endTime,
  breakDuration: shift.breakDuration,
  type: shift.type,
  daysOfWeek: shift.daysOfWeek || DEFAULT_DAYS,
  color: shift.color,
  status: shift.status,
  notes: shift.notes,
});

export function ShiftManagement() {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Shift | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<Partial<Shift>>({
    type: 'daily',
    status: 'active',
    breakDuration: 60,
    color: SHIFT_COLORS[0],
    daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
  });

  useEffect(() => {
    loadShifts();
  }, []);

  const loadShifts = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await shiftService.getShifts();
      if (response.success && response.data) {
        setShifts(response.data.map(mapApiShiftToUi));
      } else {
        setShifts([]);
      }
    } catch (err: any) {
      console.error('Error loading shifts:', err);
      setError(err.message || 'Vardiyalar yüklenirken hata oluştu');
      setShifts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const saveShift = async () => {
    if (!formData.name || !formData.code || !formData.startTime || !formData.endTime) {
      alert('Lütfen zorunlu alanları doldurun');
      return;
    }

    setIsSaving(true);
    try {
      if (editingItem) {
        const response = await shiftService.updateShift(editingItem.id, mapUiShiftToApi(formData));
        if (response.success) {
          await loadShifts();
        }
      } else {
        const response = await shiftService.createShift(mapUiShiftToApi(formData));
        if (response.success) {
          await loadShifts();
        }
      }
      closeForm();
    } catch (err: any) {
      console.error('Error saving shift:', err);
      alert(err.message || 'Kayıt sırasında hata oluştu');
    } finally {
      setIsSaving(false);
    }
  };

  const deleteShift = async (id: string) => {
    if (confirm('Bu vardiyayı silmek istediğinizden emin misiniz?')) {
      try {
        const response = await shiftService.deleteShift(id);
        if (response.success) {
          await loadShifts();
        }
      } catch (err: any) {
        console.error('Error deleting shift:', err);
        alert(err.message || 'Silme sırasında hata oluştu');
      }
    }
  };

  const openForm = (shift?: Shift) => {
    if (shift) {
      setEditingItem(shift);
      setFormData(shift);
    } else {
      setEditingItem(null);
      setFormData({
        type: 'daily',
        status: 'active',
        breakDuration: 60,
        color: SHIFT_COLORS[0],
        daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      });
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setFormData({
      type: 'daily',
      status: 'active',
      breakDuration: 60,
      color: SHIFT_COLORS[0],
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    });
  };

  const toggleDay = (day: string) => {
    const current = formData.daysOfWeek || [];
    if (current.includes(day)) {
      setFormData({ ...formData, daysOfWeek: current.filter(d => d !== day) });
    } else {
      setFormData({ ...formData, daysOfWeek: [...current, day] });
    }
  };

  const getTypeLabel = (type: Shift['type']) => {
    const types = {
      daily: 'Gündüz',
      night: 'Gece',
      rotating: 'Dönüşümlü'
    };
    return types[type];
  };

  const getDaysLabel = (days?: string[]) => {
    if (!Array.isArray(days) || days.length === 0) return 'Hafta İçi';
    if (days.length === 7) return 'Her Gün';
    if (days.length === 5 && !days.includes('Saturday') && !days.includes('Sunday')) return 'Hafta İçi';
    return days.map(d => DAYS_OF_WEEK.find(dw => dw.value === d)?.label.slice(0, 3)).join(', ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Vardiya Yönetimi</h1>
          <p className="text-gray-500 mt-1">Çalışma vardiyalarını tanımlayın</p>
        </div>
        <button
          onClick={() => openForm()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-5 h-5" />
          Yeni Vardiya Ekle
        </button>
      </div>

      {/* Loading/Error States */}
      {isLoading && (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-gray-200">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-500">Vardiyalar yükleniyor...</p>
          </div>
        </div>
      )}

      {error && !isLoading && (
        <div className="flex items-center justify-center py-12 bg-white rounded-lg border border-red-200">
          <div className="flex flex-col items-center gap-3 p-8">
            <p className="text-red-600 font-medium">{error}</p>
            <button
              onClick={loadShifts}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Tekrar Dene
            </button>
          </div>
        </div>
      )}

      {/* Shifts Grid */}
      {!isLoading && !error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {shifts.map((shift) => (
              <div key={shift.id} className="bg-white rounded-xl shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: shift.color + '20' }}
                    >
                      <Clock className="w-6 h-6" style={{ color: shift.color }} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{shift.name}</h3>
                      <p className="text-sm text-gray-500">{shift.code}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    shift.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {shift.status === 'active' ? 'Aktif' : 'Pasif'}
                  </span>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Başlangıç</span>
                    <span className="font-semibold text-gray-900">{shift.startTime}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm text-gray-600">Bitiş</span>
                    <span className="font-semibold text-gray-900">{shift.endTime}</span>
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Mola:</span> {shift.breakDuration} dk
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Tip:</span> {getTypeLabel(shift.type)}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Günler:</span> {getDaysLabel(shift.daysOfWeek)}
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-gray-200">
                  <button
                    onClick={() => openForm(shift)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
                  >
                    <Edit className="w-4 h-4" />
                    Düzenle
                  </button>
                  <button
                    onClick={() => deleteShift(shift.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                  >
                    <Trash2 className="w-4 h-4" />
                    Sil
                  </button>
                </div>
              </div>
            ))}
          </div>

          {shifts.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Henüz vardiya eklenmemiş</p>
            </div>
          )}
        </>
      )}

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-xl font-bold text-gray-900">
                {editingItem ? 'Vardiya Düzenle' : 'Yeni Vardiya Ekle'}
              </h2>
              <button onClick={closeForm} className="text-gray-400 hover:text-gray-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vardiya Adı *
                  </label>
                  <input
                    type="text"
                    value={formData.name || ''}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Sabah Vardiyası"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vardiya Kodu *
                  </label>
                  <input
                    type="text"
                    value={formData.code || ''}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="V1"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Başlangıç Saati *
                  </label>
                  <input
                    type="time"
                    value={formData.startTime || ''}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bitiş Saati *
                  </label>
                  <input
                    type="time"
                    value={formData.endTime || ''}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mola Süresi (dakika)
                  </label>
                  <input
                    type="number"
                    value={formData.breakDuration || ''}
                    onChange={(e) => setFormData({ ...formData, breakDuration: Number(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="60"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vardiya Tipi
                  </label>
                  <select
                    value={formData.type || 'daily'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value as Shift['type'] })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="daily">Gündüz</option>
                    <option value="night">Gece</option>
                    <option value="rotating">Dönüşümlü</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Renk
                  </label>
                  <div className="flex gap-2">
                    {SHIFT_COLORS.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setFormData({ ...formData, color })}
                        className={`w-8 h-8 rounded-lg border-2 transition-all ${
                          formData.color === color ? 'border-gray-900 scale-110' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Durum
                  </label>
                  <select
                    value={formData.status || 'active'}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">Aktif</option>
                    <option value="inactive">Pasif</option>
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Çalışma Günleri
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {DAYS_OF_WEEK.map(day => (
                      <label
                        key={day.value}
                        className={`flex items-center justify-center px-3 py-2 rounded-lg border-2 cursor-pointer transition-all ${
                          formData.daysOfWeek?.includes(day.value)
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.daysOfWeek?.includes(day.value) || false}
                          onChange={() => toggleDay(day.value)}
                          className="sr-only"
                        />
                        <span className="text-sm font-medium">{day.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notlar
                  </label>
                  <textarea
                    value={formData.notes || ''}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Ek bilgiler"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  onClick={closeForm}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  İptal
                </button>
                <button
                  onClick={saveShift}
                  disabled={isSaving}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                  <Save className="w-4 h-4" />
                  Kaydet
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
